/**
 * Invoice Note Card Component
 * Displays a single note with edit/delete/pin functionality
 */

import React, { useState } from 'react';
import { Card, Badge, Button, Dropdown } from 'react-bootstrap';
import { format, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';
import { invoiceNotesApi } from '../../../../../services/api/invoiceNotesApi';
import EditNoteModal from './EditNoteModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faThumbtack,
  faEdit,
  faTrash,
  faEllipsisV
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import { toast } from 'sonner';

const InvoiceNoteCard = ({ note, companyId, invoiceId, onUpdate, onDelete, isLast }) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPinning, setIsPinning] = useState(false);

  // Note type configuration
  const noteTypeConfig = {
    internal: {
      color: 'primary',
      label: 'Intern (Privat)',
      bgClass: 'bg-primary-subtle'
    },
    external: {
      color: 'success',
      label: 'Extern (Kund kan se)',
      bgClass: 'bg-success-subtle'
    },
    system: {
      color: 'secondary',
      label: 'Systemgenererad',
      bgClass: 'bg-secondary-subtle'
    }
  };

  const config = noteTypeConfig[note.note_type] || noteTypeConfig.internal;

  // Toggle pin status
  const handleTogglePin = async () => {
    try {
      setIsPinning(true);

      await invoiceNotesApi.togglePin(companyId, invoiceId, note.id);

      onUpdate();
    } catch (error) {
      console.error('Error toggling pin:', error);
      toast.error('Fel', {
        description: 'Kunde inte ändra fästning'
      });
    } finally {
      setIsPinning(false);
    }
  };

  // Delete note with confirmation
  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Radera anteckning?',
      text: 'Är du säker på att du vill radera denna anteckning?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ja, radera',
      cancelButtonText: 'Avbryt'
    });

    if (!result.isConfirmed) return;

    try {
      setIsDeleting(true);

      await invoiceNotesApi.delete(companyId, invoiceId, note.id);

      toast.success('Raderad', {
        description: 'Anteckningen har raderats'
      });

      onDelete();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Fel', {
        description: 'Kunde inte radera anteckningen'
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'dd MMM yyyy, HH:mm', { locale: sv });
    } catch {
      return dateString;
    }
  };

  const isSystemNote = note.note_type === 'system';

  return (
    <>
      <Card
        className={`mb-3 ${note.is_pinned ? 'border-warning border-2' : ''} ${isLast ? '' : ''}`}
        style={{ position: 'relative' }}
      >
        {note.is_pinned && (
          <div
            style={{
              position: 'absolute',
              top: '-10px',
              right: '20px',
              background: '#ffc107',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              zIndex: 10
            }}
          >
            <FontAwesomeIcon icon={faThumbtack} size="sm" style={{ color: 'white' }} />
          </div>
        )}

        <Card.Header className={`d-flex justify-content-between align-items-center ${config.bgClass}`}>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={config.color}>{config.label}</Badge>
            {note.is_pinned && (
              <Badge bg="warning" text="dark">Fäst</Badge>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <small className="text-muted">
              {formatDate(note.created_at)}
              {note.updated_at && note.updated_at !== note.created_at && (
                <span className="ms-1">(redigerad)</span>
              )}
            </small>
            {!isSystemNote && (
              <Dropdown align="end">
                <Dropdown.Toggle
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  style={{ textDecoration: 'none' }}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.Item onClick={handleTogglePin} disabled={isPinning}>
                    <FontAwesomeIcon icon={faThumbtack} className="me-2" />
                    {note.is_pinned ? 'Ta bort fästning' : 'Fäst överst'}
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => setShowEditModal(true)}>
                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                    Redigera
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleDelete} disabled={isDeleting} className="text-danger">
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Radera
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          <Card.Text style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>
            {note.note_text}
          </Card.Text>
        </Card.Body>
      </Card>

      <EditNoteModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        note={note}
        companyId={companyId}
        invoiceId={invoiceId}
        onSaved={onUpdate}
      />
    </>
  );
};

export default InvoiceNoteCard;
